import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Undo2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InlineWarning, ListingLinkButton } from "@/components/common/Primitives";
import { api, apiError } from "@/lib/api";
import { dateOnly, eur, eurSigned, km, num } from "@/lib/format";

const Row = ({ label, value, note, strong, tone }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-3 py-1.5">
    <div className="min-w-0">
      <div className={`text-[12px] ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {label}
      </div>
      {note && <div className="text-[10px] leading-tight text-slate-500">{note}</div>}
    </div>
    <div
      className={`mono shrink-0 text-right text-[12px] ${
        tone === "positive"
          ? "font-semibold text-emerald-700"
          : strong
            ? "font-semibold text-slate-900"
            : "text-slate-800"
      }`}
    >
      {value}
    </div>
  </div>
);

export const MarketValueDialog = ({ open, onOpenChange, row, onApplied, loading = false }) => {
  const listingId = row?.listing_id;
  const head = row?.headline || {};
  const valuation = row?.valuation || {};
  const basis = valuation.basis || {};
  const isManual = valuation.value_source === "MANUAL" || head.value_source === "MANUAL";
  const automatic = valuation.automatic_expected_market_value ?? head.expected_market_value_automatic;

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    const start = head.expected_market_value ?? automatic ?? null;
    setAmount(start === null || start === undefined ? "" : String(Math.round(start)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listingId]);

  const value = Number(String(amount).replace(/[^0-9,.-]/g, "").replace(",", "."));
  const valid = amount !== "" && !Number.isNaN(value) && value > 0;

  const run = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      const { data } = await fn();
      onApplied?.(data);
      if (label === "apply" || label === "restore") onOpenChange(false);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const apply = () =>
    valid
      ? run("apply", () =>
          api.post(`/listings/${listingId}/market-value`, {
            amount: value,
            note: null,
            automatic_value: automatic ?? null,
          })
        )
      : setError("Vul een geldige marktwaarde in euro's in.");

  const restore = () => run("restore", () => api.delete(`/listings/${listingId}/market-value`));

  const exclude = (comparableId) =>
    run(`ex-${comparableId}`, () =>
      api.post(`/listings/${listingId}/comparables/${comparableId}/exclude`, {
        reason: "Niet vergelijkbaar volgens gebruiker",
      })
    );

  const restoreComparable = (comparableId) =>
    run(`re-${comparableId}`, () =>
      api.delete(`/listings/${listingId}/comparables/${comparableId}/exclude`)
    );

  const userExcluded = (valuation.comparables_excluded || []).filter((c) => c.user_excluded);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white" data-testid="market-value-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base">
            Verwacht NL — onderbouwing en handmatige waarde
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[74vh] space-y-3 overflow-y-auto">
          {error && (
            <InlineWarning tone="danger" testid="market-value-error">
              {error}
            </InlineWarning>
          )}

          {!isManual &&
            (valuation.warnings || [])
              .filter((w) => w.startsWith("INDICATIE") || w.startsWith("LET OP"))
              .map((w) => (
                <InlineWarning
                  key={w}
                  tone={w.startsWith("LET OP") ? "danger" : "warning"}
                  testid="market-value-indicative"
                >
                  {w}
                </InlineWarning>
              ))}

          <div className="border border-slate-200">
            <Row
              label="Automatisch berekende marktwaarde"
              note={valuation.method}
              value={eur(automatic, { fallback: "Niet te bepalen" })}
            />
            {isManual && (
              <Row label="Handmatige marktwaarde" value={eur(valuation.manual_value)} />
            )}
            <Row
              label="Gebruikte waarde in de handelsberekening"
              value={eur(head.expected_market_value, { fallback: "Niet te bepalen" })}
              strong
              tone="positive"
            />
          </div>

          <div className="border border-slate-200" data-testid="market-value-basis">
            <div className="label-xs border-b border-slate-200 bg-slate-100 px-3 py-1.5">
              Gebaseerd op
            </div>
            <Row
              label="Nederlandse vergelijkingsauto's"
              note={basis.level_label || valuation.level_label || valuation.stage_label}
              value={num(valuation.n_used ?? basis.comparables ?? 0)}
            />
            <Row
              label="Matchniveau"
              note={
                valuation.level_counts
                  ? Object.entries(valuation.level_counts)
                      .map(([key, count]) => `${key}: ${count}`)
                      .join(" · ")
                  : undefined
              }
              value={valuation.level ? `${valuation.level} — ${valuation.level_short}` : "—"}
            />
            <Row
              label="Uitvoering in NL-markt"
              note={
                valuation.derivative_match === false
                  ? "Waarde afgeleid van andere uitvoeringen van hetzelfde model"
                  : undefined
              }
              value={valuation.derivative_match === false ? "Andere uitvoering" : "Zelfde uitvoering"}
            />
            <Row label="Mediaan vraagprijs" value={eur(basis.median_asking_price)} />
            <Row
              label="Kilometercorrectie"
              note={
                basis.mileage_pct_per_10000km
                  ? `${basis.mileage_learned ? "Geleerd uit marktdata" : "Standaardaanname"}: ${
                      basis.mileage_pct_per_10000km
                    }% per 10.000 km`
                  : undefined
              }
              value={eurSigned(basis.mileage_correction, { fallback: "—" })}
            />
            <Row
              label="Bouwjaarcorrectie"
              note={
                basis.age_pct_per_month
                  ? `${basis.age_learned ? "Geleerd uit marktdata" : "Standaardaanname"}: ${
                      basis.age_pct_per_month
                    }% per maand`
                  : undefined
              }
              value={eurSigned(basis.year_correction, { fallback: "—" })}
            />
            <Row
              label="Uitvoeringscorrectie"
              note={
                basis.variant_correction
                  ? "Geleerd uit de NL-marktdata van dit model (verschil in uitvoering)"
                  : undefined
              }
              value={eurSigned(basis.variant_correction, { fallback: "—" })}
            />
            <Row
              label="Betrouwbaarheid"
              note={valuation.confidence_label}
              value={`${Math.round(valuation.confidence_pct || 0)}%`}
            />
            <Row
              label="Zoekmarge"
              note={`Bouwjaar ±1 (eerste voorkeur), ±2 (tweede voorkeur)`}
              value={
                basis.km_band_primary
                  ? `± ${km(basis.km_band_primary)} / ± ${km(basis.km_band_secondary)}`
                  : "—"
              }
            />
            <Row label="Voertuigidentiteit" value={basis.identity || basis.core || "—"} />
          </div>

          {(valuation.search_steps || []).length > 0 && (
            <div className="border border-slate-200">
              <div className="label-xs border-b border-slate-200 bg-slate-100 px-3 py-1.5">
                Matchniveaus (van streng naar ruim)
              </div>
              {valuation.search_steps.map((s) => (
                <Row key={s.level || s.step} label={s.label} value={`${s.found} gevonden`} />
              ))}
            </div>
          )}

          <div>
            <label className="label-xs mb-1 block" htmlFor="market-value-amount">
              Verwacht NL handmatig instellen
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mono text-[14px] font-semibold text-slate-500">€</span>
              <input
                id="market-value-amount"
                data-testid="market-value-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="numeric"
                className="mono h-9 w-44 rounded-sm border border-slate-300 bg-white px-2 text-[14px] font-semibold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
              />
              <button
                type="button"
                onClick={apply}
                disabled={busy !== ""}
                data-testid="market-value-apply"
                className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:opacity-50"
              >
                {busy === "apply" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Toepassen
              </button>
              {isManual && (
                <button
                  type="button"
                  onClick={restore}
                  disabled={busy !== ""}
                  data-testid="market-value-restore"
                  className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Herstel automatische marktwaarde
                </button>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              Na wijziging worden bijdrage, ROI, Deal Score en de rangschikking direct opnieuw
              berekend.
            </p>
          </div>

          <div className="border border-slate-200">
            <div className="label-xs border-b border-slate-200 bg-slate-100 px-3 py-1.5">
              Gebruikte vergelijkingsauto's ({(valuation.comparables_used || []).length})
            </div>
            {loading && (
              <p className="flex items-center gap-2 px-3 py-3 text-[12px] text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Vergelijkingsauto's laden…
              </p>
            )}
            {!loading && (valuation.comparables_used || []).length === 0 && (
              <p className="px-3 py-3 text-[12px] text-slate-500">
                Geen Nederlandse vergelijkingsauto's gevonden na alle zoekstappen.
              </p>
            )}
            {(valuation.comparables_used || []).length > 0 && (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Voertuig</th>
                      <th>1e toelating</th>
                      <th>KM</th>
                      <th>Vraagprijs</th>
                      <th>Correctie</th>
                      <th>Gecorrigeerd</th>
                      <th>Score</th>
                      <th>Gewicht</th>
                      <th>Niveau</th>
                      <th>URL</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {valuation.comparables_used.map((c) => (
                      <tr key={c.listing_id} data-testid={`used-comparable-${c.listing_id}`}>
                        <td className="max-w-[190px] truncate">
                          {[c.make, c.model, c.variant].filter(Boolean).join(" ")}
                        </td>
                        <td className="num">{dateOnly(c.first_registration)}</td>
                        <td className="num">{km(c.mileage_km)}</td>
                        <td className="num">{eur(c.price)}</td>
                        <td className="num">{eurSigned(c.total_adjustment)}</td>
                        <td className="num font-semibold">{eur(c.adjusted_price)}</td>
                        <td className="num">{num(c.comparison_score, { decimals: 1 })}</td>
                        <td className="num">{num(c.weight, { decimals: 2 })}</td>
                        <td className="num" title={c.level_label}>
                          {c.level || c.stage}
                        </td>
                        <td>
                          <ListingLinkButton url={c.url} testid={`mv-comp-url-${c.listing_id}`} />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => exclude(c.listing_id)}
                            disabled={busy !== ""}
                            title="Markeer als niet vergelijkbaar"
                            data-testid={`exclude-comparable-${c.listing_id}`}
                            className="flex items-center gap-1 rounded-sm border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 transition-colors duration-150 hover:border-red-400 hover:text-red-700"
                          >
                            {busy === `ex-${c.listing_id}` ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <XCircle className="h-3 w-3" />
                            )}
                            Niet vergelijkbaar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {userExcluded.length > 0 && (
            <div className="border border-slate-200" data-testid="user-excluded-comparables">
              <div className="label-xs border-b border-slate-200 bg-slate-100 px-3 py-1.5">
                Handmatig uitgesloten ({userExcluded.length})
              </div>
              {userExcluded.map((c) => (
                <div
                  key={c.listing_id}
                  className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-1.5"
                >
                  <span className="min-w-0 truncate text-[12px] text-slate-700">
                    {[c.make, c.model, c.variant].filter(Boolean).join(" ")} · {eur(c.price)}
                  </span>
                  <button
                    type="button"
                    onClick={() => restoreComparable(c.listing_id)}
                    disabled={busy !== ""}
                    data-testid={`restore-comparable-${c.listing_id}`}
                    className="flex items-center gap-1 rounded-sm border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-blue-400 hover:text-blue-700"
                  >
                    {busy === `re-${c.listing_id}` ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Undo2 className="h-3 w-3" />
                    )}
                    Terugzetten
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
